/* eslint-disable react-refresh/only-export-components */

import { Footer } from "@/components/layout/footer";
import { Header } from "@/components/layout/header";
import { useAuth } from "@/features/auth/auth-context";
import { AdminUsersPage } from "@/pages/admin-users";
import { JobsPage } from "@/pages/jobs";
import { LoginPage } from "@/pages/login";
import { SubscriptionsPage } from "@/pages/subscriptions";
import { HeroUIProvider, ToastProvider } from "@heroui/react";
import {
  createRootRoute,
  createRoute,
  createRouter,
  NavigateOptions,
  Outlet,
  ToOptions,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useEffect } from "react";

function RootLayout() {
  const router = useRouter();
  const { isAuthenticated, isLoading } = useAuth();

  return (
    <HeroUIProvider
      navigate={(to, options) => router.navigate({ to, ...options })}
      useHref={(to) => router.buildLocation({ to }).href}
    >
      <ToastProvider />
      <div className="flex min-h-screen flex-col">
        {isAuthenticated ? <Header /> : null}
        <main className="m-auto w-full max-w-5xl flex-1 px-4 py-6">
          {isLoading ? <div className="py-10 text-center">Loading…</div> : <Outlet />}
        </main>
        <Footer />
      </div>
    </HeroUIProvider>
  );
}

function NotFoundRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    navigate({ to: "/" });
  }, [navigate]);
  return null;
}

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFoundRedirect,
});

const jobsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: function JobsRoute() {
    const { isAuthenticated, isLoading } = useAuth();
    const navigate = useNavigate();
    useEffect(() => {
      if (!isLoading && !isAuthenticated) {
        navigate({ to: "/login" });
      }
    }, [isAuthenticated, isLoading, navigate]);
    return isAuthenticated ? <JobsPage /> : null;
  },
});

const subscriptionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/playlists",
  component: function SubscriptionsRoute() {
    const { isAuthenticated, isLoading } = useAuth();
    const navigate = useNavigate();
    useEffect(() => {
      if (!isLoading && !isAuthenticated) {
        navigate({ to: "/login" });
      }
    }, [isAuthenticated, isLoading, navigate]);
    return isAuthenticated ? <SubscriptionsPage /> : null;
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: function LoginRoute() {
    const { isAuthenticated, isLoading } = useAuth();
    const navigate = useNavigate();
    useEffect(() => {
      if (!isLoading && isAuthenticated) {
        navigate({ to: "/" });
      }
    }, [isAuthenticated, isLoading, navigate]);
    return <LoginPage />;
  },
});

const adminUsersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/users",
  component: function AdminUsersRoute() {
    const { user, isAuthenticated, isLoading } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
      if (!isLoading && !isAuthenticated) {
        navigate({ to: "/login" });
        return;
      }

      if (!isLoading && user?.role !== "admin") {
        navigate({ to: "/" });
      }
    }, [user, isAuthenticated, isLoading, navigate]);

    return isAuthenticated && user?.role === "admin" ? (
      <AdminUsersPage />
    ) : null;
  },
});

const routeTree = rootRoute.addChildren([
  jobsRoute,
  subscriptionsRoute,
  adminUsersRoute,
  loginRoute,
]);

export const router = createRouter({ routeTree });

declare module "@react-types/shared" {
  interface RouterConfig {
    href: ToOptions["to"];
    routerOptions: Omit<NavigateOptions, keyof ToOptions>;
  }
}
